export interface PodOcrField {
  value: string;
  confidence: number;
}

/**
 * OCR providers that can land on a POD document.
 *  - "mock_textract": the manual/dev path where a human types raw text and a
 *    regex parse extracts fields. Never used by the real vision path.
 *  - "openrouter_vision": real LLM inference over the stored file via the
 *    vision role (see src/core/agent/model-config.ts resolveVisionModel).
 */
export type PodOcrProvider = "mock_textract" | "openrouter_vision";

export interface PodOcrResult {
  provider: PodOcrProvider;
  fields: {
    loadReference?: PodOcrField;
    deliveredAt?: PodOcrField;
    receiverName?: PodOcrField;
    signedBy?: PodOcrField;
    exceptionNotes?: PodOcrField;
  };
  overallConfidence: number;
  rawText: string;
  /**
   * Vision extraction reports signature presence directly (a model can see a
   * signature without being able to read a signer name). When set, this flag
   * — not the signedBy text field — decides the signature gate in
   * validatePodAgainstLoad.
   */
  signaturePresent?: boolean;
}

export function parsePodWithMockOcr(rawText: string): PodOcrResult {
  const fields = {
    loadReference: matchField(rawText, /(?:load|ref|reference)[:#\s]+([A-Z0-9-]+)/i),
    deliveredAt: matchField(rawText, /delivered(?: at| on)?[:\s]+([0-9TZ:.-]+)/i),
    receiverName: matchField(rawText, /receiver[:\s]+([A-Za-z .'-]+)/i),
    signedBy: matchField(rawText, /signed by[:\s]+([A-Za-z .'-]+)/i),
    exceptionNotes: matchField(rawText, /(?:exception|os&d|note)s?[:\s]+(.+)/i, 0.68),
  };
  const present = Object.values(fields).filter((field): field is PodOcrField => Boolean(field));
  const overallConfidence =
    present.length === 0
      ? 0
      : Math.round((present.reduce((sum, field) => sum + field.confidence, 0) / present.length) * 100) / 100;

  return {
    provider: "mock_textract",
    fields,
    overallConfidence,
    rawText,
  };
}

/**
 * The structured extraction the vision model is asked to return for a POD.
 * Everything except signaturePresent/confidence is optional — an unreadable
 * scan yields absent fields, never invented ones.
 */
export interface VisionPodExtraction {
  loadReference?: string;
  signaturePresent: boolean;
  deliveryDate?: string;
  notes?: string;
  confidence: number;
}

/**
 * Prompt for the vision OCR call. Deliberately does NOT include the expected
 * load reference: feeding the answer to the model invites an echoed
 * "confirmation" of the wrong document. validatePodAgainstLoad compares the
 * extraction to the real load afterwards.
 */
export const VISION_POD_OCR_PROMPT = [
  "You are extracting fields from a proof-of-delivery (POD) freight document image.",
  "Respond with ONLY a JSON object — no prose, no markdown fences — with exactly these keys:",
  '{"loadReference": string | null, "signaturePresent": boolean, "deliveryDate": string | null, "notes": string | null, "confidence": number}',
  "- loadReference: the load/reference/order number printed on the document, or null if none is legible.",
  "- signaturePresent: true only if a handwritten signature is visible on the document.",
  "- deliveryDate: the delivery date/time as written on the document (ISO if possible), or null.",
  "- notes: any exception/OS&D/damage notes, or null.",
  "- confidence: your overall extraction confidence from 0 to 1.",
  "Never guess a value you cannot actually read from the image — use null instead.",
].join("\n");

/**
 * Parse the vision model's reply into a VisionPodExtraction. Fail closed:
 * anything that is not the required JSON shape (including a non-boolean
 * signaturePresent or non-numeric confidence) returns null — the caller must
 * then land the document in an explicit OCR-unavailable state, never a
 * fabricated extraction.
 */
export function parseVisionPodExtraction(content: string): VisionPodExtraction | null {
  const stripped = content
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "");
  let parsed: unknown;
  try {
    parsed = JSON.parse(stripped);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
  const record = parsed as Record<string, unknown>;
  if (typeof record.signaturePresent !== "boolean") return null;
  if (typeof record.confidence !== "number" || !Number.isFinite(record.confidence)) return null;

  return {
    loadReference: optionalString(record.loadReference),
    signaturePresent: record.signaturePresent,
    deliveryDate: optionalString(record.deliveryDate),
    notes: optionalString(record.notes),
    confidence: clamp01(record.confidence),
  };
}

/**
 * Map a vision extraction into the PodOcrResult shape the existing document
 * seam consumes. Absent extraction values stay absent; per-field confidences
 * mirror the model's overall confidence because the vision call reports a
 * single score.
 */
export function podOcrResultFromVisionExtraction(extraction: VisionPodExtraction): PodOcrResult {
  const confidence = clamp01(extraction.confidence);
  return {
    provider: "openrouter_vision",
    fields: {
      loadReference: extraction.loadReference
        ? { value: extraction.loadReference, confidence }
        : undefined,
      deliveredAt: extraction.deliveryDate ? { value: extraction.deliveryDate, confidence } : undefined,
      exceptionNotes: extraction.notes ? { value: extraction.notes, confidence } : undefined,
    },
    overallConfidence: confidence,
    rawText: "",
    signaturePresent: extraction.signaturePresent,
  };
}

export interface PodValidationResult {
  loadIdMatches: boolean;
  signaturePresent: boolean;
  valid: boolean;
}

/**
 * P0.9: a POD may only be auto-verified when the OCR is confident AND the parsed
 * load reference matches the real load AND a signature is present. Confidence
 * alone is insufficient — a high-confidence parse of the wrong load must not pass.
 */
export function validatePodAgainstLoad(args: {
  ocr: PodOcrResult;
  loadReference: string;
  minConfidence?: number;
}): PodValidationResult {
  const minConfidence = args.minConfidence ?? 0.8;
  const ocrRef = args.ocr.fields.loadReference?.value?.trim().toLowerCase();
  const loadRef = args.loadReference.trim().toLowerCase();
  const loadIdMatches = Boolean(ocrRef) && ocrRef === loadRef;
  const signaturePresent =
    typeof args.ocr.signaturePresent === "boolean"
      ? args.ocr.signaturePresent
      : Boolean(args.ocr.fields.signedBy?.value?.trim());
  const valid = args.ocr.overallConfidence >= minConfidence && loadIdMatches && signaturePresent;
  return { loadIdMatches, signaturePresent, valid };
}

function matchField(rawText: string, pattern: RegExp, confidence = 0.86): PodOcrField | undefined {
  const match = rawText.match(pattern);
  if (!match?.[1]) return undefined;
  return {
    value: match[1].trim(),
    confidence,
  };
}

function optionalString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
